/**
 * The connect panels, shared by the setup steps, the dashboard and Connections: pairing, Claude sign-in, Shopify and
 * Triple Whale. Both connections paste credentials into one form (`CredentialForm`).
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { CLAUDE_PLANS, PASEO_LINKS } from '../../domain/copy'
import type { Connection, ConnectionField, ConnectionHelp, ConnectionWarning, Pairing, ShopifyConnectInput, ShopifyMode } from '../../domain/schemas'
import { ApiError } from '../../infra/api'
import { Out } from '../components/icons'
import { ConfirmAction } from '../components/ConfirmAction'
import { Field, Gate, LockNote, Notice, Verify } from '../components/ui'
import { Button, Inline, Text } from '../../ui'
import { useFounder } from './context'

export function GetPaseo() {
  return (
    <div className="row-btns">
      {PASEO_LINKS.map((l) => (
        <a key={l.label} className="btn btn-soft btn-sm" href={l.href} target="_blank" rel="noreferrer">
          {l.label}
        </a>
      ))}
    </div>
  )
}

/**
 * Asks for a pairing link once and shows it: a QR for a laptop, "Open in Paseo" for a phone. The link is a
 * password, so it lives only in this component's memory and is never stored.
 */
export function PairPanel({ onShown, initial }: { onShown?: () => void; /** A link already in hand (after "Remove all devices"). */ initial?: Pairing | null }) {
  const { api, reload } = useFounder()
  const [pair, setPair] = useState<Pairing | null>(initial ?? null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [notReady, setNotReady] = useState(false)

  async function show() {
    setBusy(true)
    setError(undefined)
    try {
      setPair(await api.pairing())
      onShown?.()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'not_ready') {
        setNotReady(true)
        void reload()
      } else if (e instanceof ApiError && e.code === 'no_user') setError("Your own space on the computer isn't set up yet. We'll let you know when it is.")
      else setError("We couldn't make a pairing link just now. Try again in a moment.")
    } finally {
      setBusy(false)
    }
  }

  if (notReady && !pair) return <Gate title="Your computer is almost ready.">Pairing waits for Paseo to start on it. Try again in a minute.</Gate>
  if (!pair)
    return (
      <div className="stack-sm">
        <div className="row-btns">
          <button className="btn btn-ink" type="button" onClick={show} disabled={busy}>
            {busy ? 'Making your link…' : 'Show my pairing code'}
          </button>
        </div>
        {error && <Notice>{error}</Notice>}
      </div>
    )
  return (
    <div className="stack-sm fade-in">
      <div className="pair-grid">
        <div className="pair-card">
          <h3>On your phone or iPad</h3>
          <p>Open Paseo, choose to scan or paste a pairing link, and scan this code.</p>
          <img className="qr" alt="Pairing code for Paseo" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(pair.qr)}`} />
        </div>
        <div className="pair-card">
          <h3>On your computer</h3>
          <p>
            In Paseo, go to <b>Host</b>, then <b>Add Host</b>, and choose <b>Paste Pairing Link</b>. Paste the link you copy
            here.
          </p>
          <CopyLink url={pair.url} />
          <Text size="sm" tone="muted">
            Already on the device that has Paseo? <a href={pair.url}>Open in Paseo</a> instead.
          </Text>
        </div>
      </div>
      <Text size="sm" tone="muted">
        These steps stay in Learn: <a href="/learn/pair-a-device">Pair another device</a>.
      </Text>
      <LockNote>
        The pairing link is private, like a password. Anyone who has it can reach your computer. We show it only here,
        once, and never send it by email.
      </LockNote>
    </div>
  )
}

/** Copies the pairing link for Paseo's "Paste Pairing Link". The link itself is never shown on screen. */
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div>
      <Button variant="ink" onClick={() => void navigator.clipboard.writeText(url).then(() => setCopied(true), () => setCopied(false))}>
        {copied ? 'Copied. Paste it in Paseo.' : 'Copy pairing link'}
      </Button>
    </div>
  )
}

type ClaudePhase = { kind: 'idle' } | { kind: 'await'; sessionId: string; url: string } | { kind: 'checking'; sessionId: string; url: string }

/** Sign in to Claude: open claude.ai, approve, paste the code back. We confirm it with the computer ourselves. */
export function ClaudeSignIn({ onDone }: { onDone?: () => void }) {
  const { api, refreshClaude } = useFounder()
  const [phase, setPhase] = useState<ClaudePhase>({ kind: 'idle' })
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  async function start() {
    setBusy(true)
    setError(undefined)
    // Open the tab inside the click, so a popup blocker lets it through; point it at claude.ai once we have the URL.
    const tab = window.open('', '_blank')
    try {
      const s = await api.claudeLogin()
      if (tab) tab.location.href = s.url
      setPhase({ kind: 'await', ...s })
    } catch (e) {
      tab?.close()
      setError(e instanceof ApiError && e.code === 'no_user' ? "Your own space on the computer isn't set up yet. We'll let you know when it is." : "Your computer couldn't start the sign-in just now. Try again in a moment.")
    } finally {
      setBusy(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (phase.kind !== 'await') return
    const c = code.trim()
    if (!c) return setError('Paste the code from claude.ai first.')
    setError(undefined)
    setPhase({ ...phase, kind: 'checking' })
    try {
      const r = await api.claudeCode(phase.sessionId, c)
      if (r.status === 'ok') {
        await refreshClaude()
        setCode('')
        setPhase({ kind: 'idle' })
        onDone?.()
      } else {
        setPhase({ kind: 'idle' })
        setError("That code didn't work. Start again and paste the newest code claude.ai shows you.")
      }
    } catch {
      setPhase({ kind: 'idle' })
      setError('That sign-in ended before it finished. Start again.')
    }
  }

  return (
    <div className="stack-sm">
      {phase.kind === 'idle' && (
        <div className="stack-sm fade-in">
          <div className="row-btns">
            <button className="btn btn-ink" type="button" onClick={start} disabled={busy}>
              Sign in to Claude <Out size={14} />
            </button>
          </div>
          <p className="aside">
            No Claude plan yet?{' '}
            <a href={CLAUDE_PLANS} target="_blank" rel="noreferrer">
              See Claude's plans
            </a>
            . It's the only account you might need to make.
          </p>
        </div>
      )}
      {phase.kind === 'await' && (
        <form className="well fade-in stack-sm" onSubmit={submit} noValidate>
          <p>
            <b>Approve at claude.ai, then paste the code it shows you.</b>
          </p>
          <p className="aside">
            We opened claude.ai in a new tab.{' '}
            <a href={phase.url} target="_blank" rel="noreferrer">
              Open it again
            </a>
          </p>
          <div className="form-row">
            <Field label="Code from claude.ai">
              <input className="input mono" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" spellCheck={false} placeholder="Paste the code" autoFocus />
            </Field>
            <button className="btn btn-ink" type="submit">
              Submit code
            </button>
          </div>
        </form>
      )}
      {phase.kind === 'checking' && <Gate title="Checking…">Your computer is asking Claude if it's signed in.</Gate>}
      {error && <Notice>{error}</Notice>}
    </div>
  )
}

/**
 * Shopify's names for the read-only access the starter asks for (shopify.dev, access scopes), as the API's
 * `SHOPIFY_REQUESTED_SCOPES`: the four it requires, then read_all_orders, which it only warns about.
 */
/**
 * The scopes the steps ask for, the API's required four. Not read_all_orders: the Dev Dashboard refuses it ("Contains
 * invalid scopes", wet run 2026-10-14). A merchant's own app may read older orders without it; the API checks.
 */
export const SHOPIFY_SCOPES = ['read_products', 'read_orders', 'read_inventory', 'read_customers'] as const
/** Shopify's Dev Dashboard, where every new app is made since 1 Jan 2026 (shopify.dev). */
export const DEV_DASHBOARD = 'https://dev.shopify.com/dashboard/'

export function CopyButton({ value, label = 'Copy' }: { value: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      className="btn btn-ghost btn-sm"
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).catch(() => {})
        setDone(true)
        setTimeout(() => setDone(false), 1400)
      }}
    >
      {done ? 'Copied' : label}
    </button>
  )
}

/** The short guide for each way in: four steps in the Dev Dashboard, or one for an older custom app's token. */
export function ShopifyGuide({ mode = 'clientCredentials' }: { mode?: ShopifyMode }) {
  if (mode === 'accessToken') {
    return (
      <ol className="guide">
        <li>
          <b>Copy the Admin API access token</b>
          <p>
            In your Shopify admin, go to Settings, then Apps, then Develop apps, and open your custom app. The token is
            under API credentials. Shopify shows it only once, so if you didn't save it, use a Dev Dashboard app instead.
          </p>
        </li>
      </ol>
    )
  }
  // Shopify opens the App URL after the install, with ?shop=; landing back on this page fills in the store.
  const back = `${window.location.origin}${window.location.pathname}`
  return (
    <ol className="guide">
      <li>
        <b>Create an app in your store's Dev Dashboard</b>
        <p>
          Sign in as your store's owner. Select Create app, then Start from Dev Dashboard. Your Shopify admin gets you
          there too: Settings, then Apps, then Develop apps, then Build apps in Dev Dashboard.
        </p>
        <div className="row-btns">
          <a className="btn btn-soft btn-sm" href={DEV_DASHBOARD} target="_blank" rel="noreferrer">
            Open Dev Dashboard <Out />
          </a>
        </div>
      </li>
      <li>
        <b>Fill in the version page, top to bottom</b>
        <p>
          <b>App name:</b> cpg.computer, or any name you like. <b>Version name:</b> leave it empty.
        </p>
        <p>
          <b>App URL:</b> replace https://example.com with this page's address. Shopify opens it after the install, so you
          land back here with your store filled in.
        </p>
        <span className="copyval">
          {back} <CopyButton value={back} />
        </span>
        <p>
          <b>Embed app in Shopify admin:</b> leave it off. <b>Allowed redirection URL(s):</b> leave it empty.{' '}
          <b>Use legacy install flow:</b> leave it off.
        </p>
        <p>
          <b>Scopes:</b> paste these four. They're read-only: your agent can look at your store and never change it.
        </p>
        <div className="chips">
          {SHOPIFY_SCOPES.map((s) => (
            <span key={s} className="chip">
              {s}
            </span>
          ))}
          <CopyButton value={SHOPIFY_SCOPES.join(',')} label="Copy all four" />
        </div>
        <p>
          <b>Webhooks API version:</b> leave it as it is. Then select Release.
        </p>
      </li>
      <li>
        <b>Install it on your store</b>
        <p>Select Install app, pick your store, then select Install. Shopify brings you back to this page.</p>
      </li>
      <li>
        <b>Copy the Client ID and Client secret</b>
        <p>
          They appear once a version is released. In Dev Dashboard, open your app, then its Settings, and copy both.
          These steps stay in Learn: <a href="/learn/connect-shopify">Connect Shopify</a>.
        </p>
      </li>
      <li>
        <b>Paste them below with your store address</b>
        <p>
          Your store address is the part after /store/ in your Shopify admin's address. For
          admin.shopify.com/store/your-store, it's your-store. The whole address works too.
        </p>
      </li>
    </ol>
  )
}

const STORE_FIELD: ConnectionField = { name: 'storeDomain', label: 'Store address', kind: 'text', help: 'your-store' }

/** Each mode's fields when the API's list hasn't arrived (or predates modes). */
const FALLBACK_FIELDS: Record<ShopifyMode, ConnectionField[]> = {
  clientCredentials: [
    STORE_FIELD,
    { name: 'clientId', label: 'Client ID', kind: 'text' },
    { name: 'clientSecret', label: 'Client secret', kind: 'secret' },
  ],
  accessToken: [STORE_FIELD, { name: 'accessToken', label: 'Admin API access token', kind: 'secret' }],
}

const MISSING: Record<string, string> = {
  storeDomain: 'Paste your store address first.',
  clientId: 'Paste the Client ID too.',
  clientSecret: 'Paste the Client secret too.',
  accessToken: 'Paste the Admin API access token too.',
}

/** The fields to show for a mode: the API's when it lists modes, ours otherwise. */
export function shopifyFields(connection: Connection | null, mode: ShopifyMode): ConnectionField[] {
  const listed = connection?.modes?.find((m) => m.key === mode)?.fields
  return listed?.length ? listed : FALLBACK_FIELDS[mode]
}

/**
 * Paste the store address and the app's credentials; the API checks them with Shopify (for a Dev Dashboard app, a
 * token exchange, then one read of the shop's name) and stores them encrypted. Values leave the form on submit and
 * are never shown again.
 */
export function ShopifyConnect({
  connection,
  mode = 'clientCredentials',
  onMode,
  onConnected,
}: {
  connection: Connection | null
  mode?: ShopifyMode
  onMode?: (m: ShopifyMode) => void
  onConnected: (shopName: string) => void
}) {
  const { api } = useFounder()
  const fields = shopifyFields(connection, mode)
  // Back from Shopify's install (the App URL is this page), the address carries the store: ?shop=your-store.myshopify.com.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const shop = new URLSearchParams(window.location.search).get('shop')
    return shop ? { storeDomain: shop } : ({} as Record<string, string>)
  })
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string>()
  useEffect(() => {
    setError(undefined)
  }, [values, mode])

  async function submit(e: FormEvent) {
    e.preventDefault()
    const v = Object.fromEntries(fields.map((f) => [f.name, (values[f.name] ?? '').trim()]))
    const empty = fields.find((f) => !v[f.name])
    if (empty) return setError(MISSING[empty.name] ?? `Paste the ${empty.label} too.`)
    const input: ShopifyConnectInput =
      mode === 'clientCredentials'
        ? { storeDomain: v.storeDomain!, clientId: v.clientId!, clientSecret: v.clientSecret! }
        : { storeDomain: v.storeDomain!, accessToken: v.accessToken! }
    setChecking(true)
    try {
      const r = await api.connectShopify(input)
      setValues({})
      onConnected(r.shop.name)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 400 ? err.message : "We couldn't reach your store just now. Try again in a moment.")
    } finally {
      setChecking(false)
    }
  }

  if (checking) return <Gate title="Checking your store…">We ask Shopify for your shop's name, nothing more.</Gate>
  return (
    <CredentialForm
      fields={fields}
      values={values}
      onChange={setValues}
      onSubmit={submit}
      submitLabel="Connect store"
      error={error}
      hints={{ storeDomain: 'The part after /store/ in your Shopify admin address, or the whole address.' }}
      extra={
        onMode && (
          <button className="btn btn-ghost btn-sm" type="button" onClick={() => onMode(mode === 'clientCredentials' ? 'accessToken' : 'clientCredentials')}>
            {mode === 'clientCredentials' ? 'I have an older Admin API token' : 'Use a Dev Dashboard app instead'}
          </button>
        )
      }
    />
  )
}

/** Fields whose placeholder is their example value (`help`), not "Paste the …". */
const EXAMPLE_PLACEHOLDER = new Set(['storeDomain', 'shopDomain'])

/**
 * The paste form every connection uses: one input per field (secrets as password inputs), the submit button, an
 * optional second button, and the error under it. Values never leave it except on submit.
 */
function CredentialForm({
  fields,
  values,
  onChange,
  onSubmit,
  submitLabel,
  error,
  extra,
  hints = {},
}: {
  fields: ConnectionField[]
  values: Record<string, string>
  onChange: (update: (v: Record<string, string>) => Record<string, string>) => void
  onSubmit: (e: FormEvent) => void
  submitLabel: string
  error?: string
  extra?: ReactNode
  /** A line under a field, by name; secrets get the stored-encrypted line by default. */
  hints?: Record<string, string>
}) {
  return (
    <form className="well stack-sm" onSubmit={onSubmit} noValidate>
      {fields.map((f) => (
        <Field key={f.name} label={f.label} hint={hints[f.name] ?? (f.kind === 'secret' ? 'Stored encrypted. We never show it again.' : undefined)}>
          <input
            className="input mono"
            type={f.kind === 'secret' ? 'password' : 'text'}
            value={values[f.name] ?? ''}
            onChange={(e) => onChange((x) => ({ ...x, [f.name]: e.target.value }))}
            placeholder={EXAMPLE_PLACEHOLDER.has(f.name) ? (f.help ?? '') : `Paste the ${f.label}`}
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
          />
        </Field>
      ))}
      <div className="row-btns">
        <button className="btn btn-ink" type="submit">
          {submitLabel}
        </button>
        {extra}
      </div>
      {error && <Notice>{error}</Notice>}
    </form>
  )
}

/** Connected, and any warning the API sent with it (today: orders only from the last 60 days), with its fix. */
/** "15 Oct, 17:47". */
const checkedOn = (at: string) => new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

/**
 * The connected store, what Shopify granted at the last check and when, and any warning. With `onChecked`, a "Check
 * again" button re-runs the check on the saved keys: after a new version is released in Shopify, nothing to paste.
 */
export function ShopifyConnected({
  name,
  warnings = [],
  scopes,
  checkedAt,
  onChecked,
}: {
  name: string
  warnings?: ConnectionWarning[]
  scopes?: string[] | null
  checkedAt?: string | null
  onChecked?: () => void
}) {
  const seen = [scopes?.length ? `Shopify gave it ${scopes.join(', ')}.` : null, checkedAt ? `Checked ${checkedOn(checkedAt)}.` : null].filter(Boolean)
  return (
    <>
      <Verify title={`Connected to ${name}.`} sub="Your app now reads your real data." />
      {seen.length > 0 && (
        <Text size="sm" tone="muted">
          {seen.join(' ')}
        </Text>
      )}
      {warnings.map((w) => (
        <Notice key={w.code}>{w.message}</Notice>
      ))}
      {onChecked && <ShopifyRecheck onChecked={onChecked} />}
    </>
  )
}

/** Check again with the saved keys. A refusal says why in Shopify's terms; the card then asks for a reconnect. */
function ShopifyRecheck({ onChecked }: { onChecked: () => void }) {
  const { api } = useFounder()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  async function check() {
    setBusy(true)
    setError(undefined)
    try {
      await api.recheckShopify()
      onChecked()
    } catch (e) {
      setError(e instanceof ApiError && e.status === 400 ? e.message : "We couldn't reach your store just now. Try again in a moment.")
      if (e instanceof ApiError && e.status === 400) onChecked()
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <Inline>
        <Button variant="ghost" size="sm" onClick={check} disabled={busy}>
          {busy ? 'Checking…' : 'Check again'}
        </Button>
        <Text size="sm" tone="muted">
          Released a new version in Shopify? Check again to see what it gives.
        </Text>
      </Inline>
      {error && <Notice>{error}</Notice>}
    </>
  )
}

/** A connected connection on Connections: what it reads, Replace the key, and any other action (Disconnect). */
export function ConnectedPanel({ children, onReplace, actions }: { children: ReactNode; onReplace: () => void; actions?: ReactNode }) {
  return (
    <div className="stack-sm">
      {children}
      <div className="row-btns">
        <button className="btn btn-ghost btn-sm" type="button" onClick={onReplace}>
          Replace the key
        </button>
        {actions}
      </div>
    </div>
  )
}

/** What disconnecting Shopify does, in the founder's words: the keys go from the app and the computer, the app redeploys. */
export const SHOPIFY_DISCONNECT_QUESTION =
  'Disconnect Shopify? We remove its keys from your app and your computer, and your app redeploys without them, so it shows example data until you connect again. The app you made in Shopify stays there until you delete it.'

/**
 * Disconnect on a connected connection: asks first, then removes its keys (DELETE /api/founder/connections/:key).
 * Already gone (404 not_connected) counts as done, so a second tab can't strand the card.
 */
export function DisconnectAction({ connectionKey, name, question, onDone }: { connectionKey: string; name: string; question: string; onDone: () => void }) {
  const { api } = useFounder()
  const [working, setWorking] = useState(false)
  const [failed, setFailed] = useState(false)

  async function disconnect() {
    setWorking(true)
    setFailed(false)
    try {
      await api.disconnect(connectionKey)
      onDone()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'not_connected') onDone()
      else setFailed(true)
    } finally {
      setWorking(false)
    }
  }

  return (
    <ConfirmAction label="Disconnect" tone="ghost" question={question} working={working} onConfirm={() => void disconnect()}>
      {failed && <Notice tone="bad">We couldn't disconnect {name} just now. Try again in a moment.</Notice>}
    </ConfirmAction>
  )
}

// ---------- Triple Whale (board triple-whale-r1): an API key per founder, Summary Page: Read only ----------

/** The API's steps and fields, for when its list hasn't arrived. The API's own copy wins when it has. */
const TRIPLE_WHALE_HELP: ConnectionHelp = {
  steps: [
    'In Triple Whale, open Data, then APIs, and select Generate. Some accounts show it as Settings, then API Keys.',
    'Describe it as cpg.computer and tick only Summary Page: Read. That lets your app read your numbers and change nothing.',
    'Select Generate and copy the key. Triple Whale shows it once.',
    'Paste it below with your shop ID: the shop-id in your Triple Whale address, like your-store.myshopify.com.',
  ],
  note: 'Every paid Triple Whale plan includes API access. The key belongs to whoever made it, so it stops working if they leave your Triple Whale account.',
  link: { label: 'Open Data > APIs', url: 'https://app.triplewhale.com/api-keys' },
}
const TRIPLE_WHALE_FIELDS: ConnectionField[] = [
  { name: 'shopDomain', label: 'Shop ID', kind: 'text', help: 'your-store.myshopify.com' },
  { name: 'apiKey', label: 'API key', kind: 'secret' },
]

/**
 * Triple Whale's card on Connections: the steps and the form, or the connected line with Replace and Disconnect.
 * `shopifyConnected` lets the shop ID stay blank (the API then uses the Shopify store's address).
 */
export function TripleWhaleCard({ connection, shopifyConnected, onChange }: { connection: Connection; shopifyConnected: boolean; onChange: () => void }) {
  const [replacing, setReplacing] = useState(false)
  const [justConnected, setJustConnected] = useState<string | null>(null)
  const connected = connection.status === 'connected' && !replacing

  if (connected) {
    return (
      <ConnectedPanel
        onReplace={() => setReplacing(true)}
        actions={
          <DisconnectAction
            connectionKey="triplewhale"
            name="Triple Whale"
            question="Disconnect Triple Whale? Your app goes back to example numbers. The key keeps working in Triple Whale until you revoke it there."
            onDone={() => (setJustConnected(null), onChange())}
          />
        }
      >
        <Verify title="Connected to Triple Whale." sub={`Your app reads the Summary Page for ${justConnected ?? connection.detail ?? 'your shop'}.`} />
      </ConnectedPanel>
    )
  }
  const help = connection.help ?? TRIPLE_WHALE_HELP
  return (
    <>
      {connection.status === 'error' && <Notice>Triple Whale stopped answering with the saved key. Connect it again below.</Notice>}
      <ol className="guide">
        {help.steps.map((step, i) => (
          <li key={step}>
            <p>{step}</p>
            {i === 0 && help.link && (
              <a className="btn btn-soft btn-sm" href={help.link.url} target="_blank" rel="noreferrer">
                {help.link.label} <Out />
              </a>
            )}
          </li>
        ))}
      </ol>
      {help.note && <LockNote>{help.note}</LockNote>}
      <TripleWhaleConnect
        connection={connection}
        shopifyConnected={shopifyConnected}
        onConnected={(shop) => {
          setJustConnected(shop)
          setReplacing(false)
          onChange()
        }}
      />
    </>
  )
}

const TW_MISSING: Record<string, string> = {
  shopDomain: 'Paste your shop ID first.',
  apiKey: 'Paste the API key too.',
}

/** The shop ID and key; the API checks the key, then reads one day of the Summary Page, then stores both. */
function TripleWhaleConnect({
  connection,
  shopifyConnected,
  onConnected,
}: {
  connection: Connection
  shopifyConnected: boolean
  onConnected: (shop: string) => void
}) {
  const { api } = useFounder()
  const fields = connection.modes?.find((m) => m.key === 'apiKey')?.fields ?? TRIPLE_WHALE_FIELDS
  const [values, setValues] = useState<Record<string, string>>({})
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string>()
  useEffect(() => {
    setError(undefined)
  }, [values])

  async function submit(e: FormEvent) {
    e.preventDefault()
    const shopDomain = (values.shopDomain ?? '').trim()
    const apiKey = (values.apiKey ?? '').trim()
    if (!shopDomain && !shopifyConnected) return setError(TW_MISSING.shopDomain)
    if (!apiKey) return setError(TW_MISSING.apiKey)
    setChecking(true)
    try {
      const r = await api.connectTripleWhale(shopDomain ? { apiKey, shopDomain } : { apiKey })
      setValues({})
      onConnected(r.shop)
    } catch (err) {
      setError(err instanceof ApiError && (err.status === 400 || err.code === 'triple_whale_unavailable') ? err.message : "We couldn't reach Triple Whale just now. Try again in a moment.")
    } finally {
      setChecking(false)
    }
  }

  if (checking) return <Gate title="Checking your key…">We ask Triple Whale for one day of your Summary Page, and keep none of it.</Gate>
  return (
    <CredentialForm
      fields={fields}
      values={values}
      onChange={setValues}
      onSubmit={submit}
      submitLabel="Connect Triple Whale"
      error={error}
      hints={shopifyConnected ? { shopDomain: "Leave it blank to use your Shopify store's address." } : {}}
    />
  )
}
